import { useEffect, useRef } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { getQueryResultStatus, useQueryResult } from '../../../../hooks/QueryBuilder/useQueryResult';
import { useQueryResultRefresh } from '../../../../hooks/QueryBuilder/useQueryResultRefresh';
import { useLanguage } from '../../../../context/LanguageContext';
import CountCards from '../../../Clients/QueryBuilder/components/CountCards';

const POLL_INTERVAL_MS = 5000;

interface TargetingResultPanelProps {
  queryId: number;
  description: string;
  onNewTargeting: () => void;
  onCount: (count: number) => void;
}

function TargetingResultPanel({ queryId, description, onNewTargeting, onCount }: TargetingResultPanelProps) {
  const { t, formatLocale } = useLanguage();
  const { fetchQueryResult, loading, error, result } = useQueryResult();
  const status = getQueryResultStatus(result);
  const isPending = status === 'pending';
  const isCompleted = status === 'completed';
  const isError = status === 'error';
  const isExpired = status === 'expired';
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  useEffect(() => {
    fetchQueryResult(queryId);
  }, [queryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useQueryResultRefresh(queryId, () => fetchQueryResult(queryId, { keepPreviousResult: true }), isPending ? POLL_INTERVAL_MS : null);

  const count = isCompleted && typeof result?.count === 'number' ? result.count : null;

  useEffect(() => {
    if (count !== null) onCountRef.current(count);
  }, [count]);

  return (
    <Box>
      <Alert icon={<CheckCircleIcon fontSize="small" />} severity="success" sx={{ mb: 2, fontSize: '0.8rem' }}>
        Requête <strong>{description}</strong>
      </Alert>

      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        {loading && !result && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t('pages.clients.queryBuilder.resultDialog.loading')}
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {isPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={22} thickness={5} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('pages.clients.queryBuilder.resultDialog.countingTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('pages.clients.queryBuilder.resultDialog.countingMessage')}
              </Typography>
            </Box>
          </Box>
        )}

        {isError && (
          <Alert severity="error">
            <strong>{t('pages.clients.queryBuilder.resultDialog.failedTitle')}</strong>{' '}
            {result?.message || t('pages.clients.queryBuilder.resultDialog.genericErrorMessage')}
          </Alert>
        )}

        {isExpired && (
          <Alert severity="warning">{t('pages.clients.queryBuilder.resultDialog.expiredMessage')}</Alert>
        )}

        {count !== null && (
          <Box sx={{ textAlign: 'center', mb: result?.listCount?.length ? 2.5 : 0 }}>
            <PeopleAltIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {count.toLocaleString(formatLocale)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pages.clients.queryBuilder.resultDialog.globalCount')}
            </Typography>
          </Box>
        )}

        {isCompleted && result?.listCount && result.listCount.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              {t('pages.clients.queryBuilder.resultDialog.channelBreakdown')}
            </Typography>
            <CountCards listCount={result.listCount} total={result.count} />
          </Box>
        )}
      </Paper>

      <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={onNewTargeting} sx={{ textTransform: 'none' }}>
        Nouveau ciblage
      </Button>
    </Box>
  );
}

export default TargetingResultPanel;
